import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/mkt-promotions/adherence/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetMyAdherenceUpdateStatusPathInput = paths[url]['put']['parameters']['path'];
export type GetMyAccountAddressesEditBodyInput = paths[url]['put']['requestBody']['content']['application/json'];

export interface GetMyAdherenceUpdateStatusSuccessResponse {
    message: string;
}

/**
 * Requests endpoint to update a promotion's adherence status.
 */
export const useGetMyAdherenceUpdateStatus = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [validationErrors, setValidationErrors] = useState<any | null>(null);

    const request = async (pathProps: GetMyAdherenceUpdateStatusPathInput, bodyProps: GetMyAccountAddressesEditBodyInput) => {
        setLoading(true);
        setSuccess(null);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', pathProps.uuid || '')}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...bodyProps }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;

                throw error;
            }

            const data = await response.json();

            setSuccess(data.message);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, success, errorMessage, validationErrors, lastResponseTime };
};
